import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import PlayTable from "../PlayTable/PlayTable";
import WeekResults from "./WeekResults";
import { weeklyBoards, computeLeaderboard } from "./weeklyBoards";
import { getWeeklyTournament, loadMyEntries, loadAllEntries, saveEntry, loadPairWeek } from "./tournamentData";
import { sendPlayEvent } from "../../utils/analytics";
import { TOURNAMENT_POINTS } from "../../data/thisMonthBonus";
import "./WeeklyTournament.css";

// Leaderboard privacy: show "First L" (first name + surname initial), e.g.
// "Paul Dalley" -> "Paul D". Single-word names are left as-is.
function shortName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "Player";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}`;
}

// A pair (partner hub, Paul 18 Oct 2026) shows as "Paul D & Jane S".
function entryName(p) {
  return p.pairNames ? p.pairNames.map(shortName).join(" & ") : shortName(p.displayName);
}

function WeeklyTournament({ uid, displayName, subscriptionActive, isAdmin, authReady }) {
  const isLocalhost =
    typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)/.test(window.location.hostname);
  const mockUnsub =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("mockUnsub") === "1";
  const canView = (isLocalhost && !mockUnsub) || isAdmin || !!subscriptionActive;

  const [weekId, setWeekId] = useState(null);
  const [boards, setBoards] = useState(null);
  const [myEntries, setMyEntries] = useState({});
  const [idx, setIdx] = useState(0); // index of the board currently shown
  const [view, setView] = useState("play"); // "play" | "leaderboard" | "results"
  const [leaderboard, setLeaderboard] = useState(null);
  const [error, setError] = useState(null);
  const [pairWeek, setPairWeek] = useState(null); // { pairId, partner } when this week is being played as a pair
  // Opened inside the Game Plan page's panel (an iframe): it is told after each board so its
  // card and the ladder update, and "Back to Game Plan" closes the panel.
  const framed = typeof window !== "undefined" && window.self !== window.top;
  const tellParent = useCallback((type, extra) => {
    try { window.parent.postMessage({ type, ...(extra || {}) }, "*"); } catch (_) {}
  }, []);

  // Load the week's boards + this player's progress; resume at the first unplayed board.
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      try {
        const t = await getWeeklyTournament();
        const bds = weeklyBoards(t.seed, undefined, t.weekId);
        const mine = uid ? await loadMyEntries(t.weekId, uid) : {};
        const pair = uid ? await loadPairWeek(t.weekId, uid) : null;
        if (cancelled) return;
        setPairWeek(pair);
        setWeekId(t.weekId);
        setBoards(bds);
        setMyEntries(mine);
        const firstUndone = bds.findIndex((b) => !mine[b.boardNo]);
        setIdx(firstUndone === -1 ? bds.length : firstUndone);
      } catch (e) {
        if (!cancelled) setError("Couldn't load the tournament (are you signed in as a member?).");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, uid]);

  const onResult = useCallback(
    async (record) => {
      if (!weekId || !boards || !boards[idx]) return;
      const b = boards[idx];
      try {
        const saved = await saveEntry(weekId, uid, displayName, b.boardNo, record);
        setMyEntries((m) => ({ ...m, [b.boardNo]: saved }));
        if (framed) {
          const played = new Set([...Object.keys(myEntries).map(Number), b.boardNo]).size;
          tellParent("bc-month-result", { tournament: { boards: played, done: played >= boards.length } });
        }
        sendPlayEvent("weekly_board_complete", { board_no: b.boardNo });
      } catch (e) {
        setError("Couldn't save your result (are you signed in as a member?).");
      }
    },
    [weekId, boards, idx, uid, displayName, framed, tellParent, myEntries]
  );

  const nextBoard = useCallback(() => setIdx((i) => i + 1), []);

  const openLeaderboard = useCallback(async () => {
    setView("leaderboard");
    setLeaderboard(null);
    try {
      setLeaderboard(computeLeaderboard(await loadAllEntries(weekId)));
    } catch (e) {
      setLeaderboard({ players: [], datums: new Map(), impByUidBoard: {} });
    }
  }, [weekId]);

  // ── gates ───────────────────────────────────────────────────────────────────
  if (!authReady) return <div className="wt-page"><p className="wt-wait">Checking access…</p></div>;
  if (!canView) {
    return (
      <div className="wt-page wt-page--locked">
        <Helmet><title>Weekly Tournament — Members</title><meta name="robots" content="noindex" /></Helmet>
        <div className="wt-lockCard">
          <h1 className="wt-lockTitle">The Weekly Tournament is for members</h1>
          <Link to="/membership?redirectTo=/weekly" className="wt-btn wt-btn--primary">View membership</Link>
        </div>
      </div>
    );
  }

  if (!boards) return <div className="wt-page"><p className="wt-wait">Loading…</p></div>;

  // This week is being played with a partner: a week is solo OR as a pair, never both.
  if (pairWeek && view === "play") {
    return (
      <div className="wt-page">
        <Helmet><title>Weekly Tournament — Bridge Champions</title><meta name="robots" content="noindex" /></Helmet>
        <div className="wt-bar">
          <h1 className="wt-title">Weekly Tournament</h1>
          <span className="wt-week">Week {weekId || "…"}</span>
        </div>
        <p className="wt-done">You're playing this week's boards with {pairWeek.partner}.</p>
        <div className="wt-actions">
          <Link to={`/partner/${pairWeek.pairId}/weekly`} className="wt-btn wt-btn--primary">Go to your table</Link>
          <button className="wt-btn" onClick={openLeaderboard}>View leaderboard</button>
        </div>
      </div>
    );
  }

  // Leaderboard screen — reused as a full page (after all boards are done) AND as an
  // overlay laid over the live board. Rendering it as an overlay (rather than an early
  // return that swaps out the play tree) keeps PlayTable mounted, so the hand in
  // progress is preserved when the player opens the leaderboard and comes back.
  const renderLeaderboard = () => {
    const players = leaderboard ? leaderboard.players : null;
    const myRank = players ? players.findIndex((p) => p.uid === uid) : -1;
    return (
      <div className="wt-page">
        <Helmet><title>Weekly Tournament — Bridge Champions</title><meta name="robots" content="noindex" /></Helmet>
        <div className="wt-bar">
          <button className="wt-btn" onClick={() => setView("play")}>← Back</button>
          <h1 className="wt-title">Weekly Tournament</h1>
          <span className="wt-week">Week {weekId || "…"}</span>
        </div>
        {!players ? (
          <p className="wt-wait">Loading…</p>
        ) : players.length === 0 ? (
          <p className="wt-empty">No results yet this week.</p>
        ) : (
          <ol className="wt-board">
            {players.slice(0, 10).map((p, i) => (
              <li key={p.uid} className={`wt-row ${p.uid === uid ? "wt-row--me" : ""}`}>
                <span className="wt-rank">{i + 1}</span>
                <span className="wt-name">{entryName(p)}</span>
                <span className="wt-imps">{p.totalImps >= 0 ? "+" : ""}{p.totalImps} IMPs</span>
              </li>
            ))}
            {myRank >= 10 && (
              <li className="wt-row wt-row--me wt-row--you">
                <span className="wt-rank">{myRank + 1}</span>
                <span className="wt-name">You</span>
                <span className="wt-imps">{players[myRank].totalImps >= 0 ? "+" : ""}{players[myRank].totalImps} IMPs</span>
              </li>
            )}
          </ol>
        )}
      </div>
    );
  };

  // Results & hands (Paul, 2 Oct 2026): per-board scores, review of played boards, past weeks.
  const renderResults = () => (
    <WeekResults initialWeekId={weekId} uid={uid} isAdmin={isAdmin} onClose={() => setView("play")} />
  );

  // ── every board played ────────────────────────────────────────────────────────────
  if (idx >= boards.length) {
    if (view === "leaderboard") return renderLeaderboard();
    if (view === "results") return renderResults();
    return (
      <div className="wt-page">
        <Helmet><title>Weekly Tournament — Bridge Champions</title><meta name="robots" content="noindex" /></Helmet>
        <div className="wt-bar">
          <h1 className="wt-title">Weekly Tournament</h1>
          <span className="wt-week">Week {weekId || "…"}</span>
        </div>
        <p className="wt-done">You've played all {boards.length} boards this week.</p>
        <p className="wt-wellDone">Well done <b>+{TOURNAMENT_POINTS}</b></p>
        <div className="wt-actions">
          <button className="wt-btn wt-btn--primary" onClick={() => setView("results")}>Your results &amp; hands</button>
          <button className="wt-btn" onClick={openLeaderboard}>View leaderboard</button>
          {framed
            ? <button className="wt-btn" onClick={() => tellParent("bc-month-close")}>Back to Game Plan</button>
            : <Link to="/just-play" className="wt-btn">Back to Just Play</Link>}
        </div>
      </div>
    );
  }

  // ── play the current board (continuous flow) ─────────────────────────────────
  const b = boards[idx];
  const last = idx === boards.length - 1;
  return (
    <div className="wt-page wt-play">
      <Helmet><title>Weekly Tournament — Bridge Champions</title><meta name="robots" content="noindex" /></Helmet>
      <div className="wt-playBar">
        <span className="wt-title">Weekly Tournament</span>
        <span className="wt-playMeta">Board {b.boardNo} of {boards.length}</span>
        <button className="wt-link" onClick={() => setView("results")}>Results &amp; hands</button>
        <button className="wt-link wt-link--next" onClick={openLeaderboard}>Leaderboard</button>
      </div>
      <p className="wt-pointsLine">Completing this will give you <b>+{TOURNAMENT_POINTS} points</b></p>
      <PlayTable
        key={b.boardNo}
        embedded
        singleDeal
        dealOverride={b}
        persistKey={weekId ? `wt:${weekId}:${b.boardNo}` : null}
        exitLabel={last ? "Finish" : "Next board"}
        onResult={onResult}
        onExit={() => {
          if (last) sendPlayEvent("weekly_tournament_finished");
          nextBoard();
        }}
      />
      {view === "leaderboard" && <div className="wt-lbOverlay">{renderLeaderboard()}</div>}
      {view === "results" && <div className="wt-lbOverlay">{renderResults()}</div>}
    </div>
  );
}

export default connect((state) => ({
  uid: state.auth?.uid,
  displayName: state.auth?.displayName || "Player",
  subscriptionActive: state.auth?.subscriptionActive === true,
  isAdmin: state.auth?.a === true,
  authReady: state.auth?.authReady === true,
}))(WeeklyTournament);
